import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RippCore } from "../v2/RippCore";
import { C, Chip, Readout, biasRGB, fmt, label } from "./shared";

/* ENTER THE CORE — sticky scroll scene. The Core's mode + agent connections are the LIVE system state. */
const STAGE_MODE = { scan: "scanning", pattern: "analyzing", confluence: "confluence", risk: "risk", execution: "executing", manage: "position", scalein: "scalein" };
const LINES = ["IT SEES.", "IT THINKS.", "IT MANAGES.", "IT ADAPTS."];

const Line = ({ p, i, text }) => {
  const a = 0.12 + i * 0.16;
  const o = useTransform(p, [a, a + 0.08], [0, 1]);
  const x = useTransform(p, [a, a + 0.08], [-24, 0]);
  return <motion.div style={{ opacity: o, x }} className="font-display text-3xl font-black tracking-tight text-white md:text-5xl" data-testid={`core-line-${i}`}>{text}</motion.div>;
};

export const coreAgents = (pulse) => (pulse?.agents?.roster || []).map((name) => {
  const sp = pulse?.desk?.specialists?.[name];
  const running = (pulse?.desk?.running || []).includes(name);
  return { name, state: running ? "analyzing" : sp ? "signal" : "idle", tone: sp?.bias === "bullish" ? "good" : sp?.bias === "bearish" ? "bad" : undefined, bias: sp?.bias, confidence: sp?.confidence };
});

export const EnterTheCore = ({ pulse }) => {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const scale = useTransform(p, [0, 0.55], [0.55, 1]);
  const ringO = useTransform(p, [0.1, 0.5], [0, 1]);
  const readO = useTransform(p, [0.55, 0.75], [0, 1]);
  const stage = pulse?.stage || "scan";
  const agents = coreAgents(pulse);
  const cons = pulse?.desk?.consensus;
  const sym = pulse?.symbols?.find((s) => s.symbol === pulse.focus);
  return (
    <section ref={ref} id="core" data-testid="enter-the-core" className="relative h-[240vh] border-t border-white/[0.06]">
      <div className="sticky top-0 flex h-screen items-center overflow-hidden">
        <div className="pointer-events-none absolute inset-0 ripp-dotgrid opacity-40" />
        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-6 md:grid-cols-[1fr_auto_1fr]">
          <div className="space-y-3">
            <div className="mb-6 font-mono text-[10px] tracking-[0.3em] text-[#38BDF8]">01 — ENTER THE CORE</div>
            {LINES.map((l, i) => <Line key={l} p={p} i={i} text={l} />)}
          </div>
          <motion.div style={{ scale }} className="relative grid place-items-center" data-testid="core-stage" data-stage={stage}>
            <motion.div style={{ opacity: ringO }} className="pointer-events-none absolute inset-[-8%] rounded-full border border-[#38BDF8]/10" />
            <RippCore mode={STAGE_MODE[stage] || "scanning"} agents={agents} size={360} />
          </motion.div>
          <motion.div style={{ opacity: readO }} className="space-y-5 md:pl-6" data-testid="core-readout">
            <div className="font-mono text-[9px] tracking-[0.26em] text-[#7F8BA3]">LIVE SYSTEM STATE · NOT A DEMO</div>
            <div className="grid grid-cols-2 gap-4">
              <Readout k="FOCUS" v={sym ? `${sym.symbol} ${fmt(sym.price)}` : "—"} rgb={C.cyan} testid="core-focus" />
              <Readout k="STAGE" v={stage.toUpperCase()} rgb={C.amber} testid="core-stage-readout" />
              <Readout k="DESK CALL" v={cons ? `${cons.direction?.toUpperCase()} ${Math.round(cons.confidence)}%` : "NO CALL"} rgb={cons ? biasRGB(cons.direction) : C.text2} />
              <Readout k="AGREEMENT" v={cons?.agreement?.replace("_", " ") || "—"} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {agents.map((a) => <Chip key={a.name} rgb={a.state === "analyzing" ? C.cyan : a.bias ? biasRGB(a.bias) : C.text2} testid={`core-agent-${a.name}`}>{label(a.name)} · {a.state === "analyzing" ? "ANALYZING" : a.bias ? `${a.bias.toUpperCase()} ${Math.round(a.confidence)}%` : "STANDBY"}</Chip>)}
            </div>
            <p className="max-w-sm font-body text-sm font-light leading-relaxed text-white/55">
              The Core breathes with the system. Rings accelerate when agents run, connections brighten on consensus, colour shifts to amber under risk review and emerald while a position is being managed.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
